import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header1 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template1' },
        { label: 'Shop', path: '/template1/shop' },
        { label: 'About', path: '/template1/about' },
        { label: 'Contact', path: '/template1/contact' },
        { label: 'Orders', path: '/template1/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <header
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E5E7EB]'
                        : 'bg-white border-b border-transparent'
                }`}
                style={{ fontFamily: "'Poppins', sans-serif" }}
            >
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 md:h-20">
                        {/* Mobile: Hamburger */}
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="md:hidden p-2 text-[#1A1A1A] hover:text-[#2D6A4F] transition-colors"
                        >
                            <Menu size={22} />
                        </button>

                        {/* Desktop: Nav Links (Left) */}
                        <nav className="hidden md:flex items-center gap-8">
                            {navLinks.slice(0, 3).map((link) => (
                                <button
                                    key={link.label}
                                    onClick={() => handleNavClick(link.path)}
                                    className="text-sm font-medium text-[#1A1A1A] hover:text-[#2D6A4F] transition-colors tracking-wide"
                                >
                                    {link.label}
                                </button>
                            ))}
                        </nav>

                        {/* Logo (Center) */}
                        <h1
                            onClick={() => navigate('/template1')}
                            className="text-xl md:text-2xl font-semibold text-[#2D6A4F] cursor-pointer tracking-wider"
                            style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                            PRIYA
                        </h1>

                        {/* Desktop: Nav Links (Right) */}
                        <nav className="hidden md:flex items-center gap-8">
                            {navLinks.slice(3).map((link) => (
                                <button
                                    key={link.label}
                                    onClick={() => handleNavClick(link.path)}
                                    className="text-sm font-medium text-[#1A1A1A] hover:text-[#2D6A4F] transition-colors tracking-wide"
                                >
                                    {link.label}
                                </button>
                            ))}
                        </nav>

                        {/* Right Icons */}
                        <div className="flex items-center gap-3 md:gap-5">
                            <Search className="hidden md:block w-5 h-5 text-[#1A1A1A] cursor-pointer hover:text-[#2D6A4F] transition-colors" />
                            <User
                                className="hidden md:block w-5 h-5 text-[#1A1A1A] cursor-pointer hover:text-[#2D6A4F] transition-colors"
                                onClick={() => navigate('/template1/profile')}
                            />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template1/cart')}>
                                <ShoppingCart className="w-5 h-5 text-[#1A1A1A] hover:text-[#2D6A4F] transition-colors" />
                                {cartCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-[#2D6A4F] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">
                                        {cartCount}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-[60] md:hidden">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div
                        className="absolute top-0 left-0 bottom-0 w-[280px] bg-white shadow-xl"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
                            <h2 className="text-lg font-semibold text-[#2D6A4F] tracking-wider">PRIYA</h2>
                            <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-[#6B7280] hover:text-[#1A1A1A]">
                                <X size={22} />
                            </button>
                        </div>
                        <nav className="p-5 space-y-1">
                            {navLinks.map((link) => (
                                <button
                                    key={link.label}
                                    onClick={() => handleNavClick(link.path)}
                                    className="w-full text-left px-4 py-3 text-[#1A1A1A] hover:bg-[#F8FAF8] hover:text-[#2D6A4F] transition-colors text-sm font-medium rounded-lg"
                                >
                                    {link.label}
                                </button>
                            ))}
                            <div className="border-t border-[#E5E7EB] pt-3 mt-3">
                                <button
                                    onClick={() => handleNavClick('/template1/login')}
                                    className="w-full text-left px-4 py-3 text-[#1A1A1A] hover:bg-[#F8FAF8] hover:text-[#2D6A4F] transition-colors text-sm font-medium rounded-lg"
                                >
                                    Sign In
                                </button>
                                <button
                                    onClick={() => handleNavClick('/template1/profile')}
                                    className="w-full text-left px-4 py-3 text-[#1A1A1A] hover:bg-[#F8FAF8] hover:text-[#2D6A4F] transition-colors text-sm font-medium rounded-lg"
                                >
                                    My Account
                                </button>
                            </div>
                        </nav>
                    </div>
                </div>
            )}

            {/* Spacer for fixed header */}
            <div className="h-16 md:h-20" />
        </>
    );
};

export default Header1;
